import { Card, CardBody } from "@progress/kendo-react-layout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useUserOrganizationsStore } from "store";
import { getVesselQuotasApi } from "api/vessel";
import { IVesselQuotaData } from "interface/vessel";
import { formatNorwegianNumber } from "utils/helper";
import styles from "styles.module.css";
import style from "./style.module.css";
import { Grid, GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import { CustomRowRender } from "ui/grid/CustomGrid";
import { CLASSNAME, COLOR } from "utils/constant";
import { PageTypePath } from "utils/pages";

const AccountNameCell = (props: GridCellProps) => {
	const item: IVesselQuotaData = props.dataItem;
	const navigate = useNavigate();

	const handleClick = () => {
		const params = new URLSearchParams({
			accountNumber: item.accountNumber,
			validFrom: item.validFrom,
			validTo: item.validTo ?? "",
			source: item.source ?? "",
			rg: item.reportGuid ?? "",
		});
		navigate(`/${PageTypePath.QuotaTransactions}?${params.toString()}`);
		//window.open(`/${PageTypePath.QuotaTransactions}?${params.toString()}`, "_blank");
	};

	return (
		<td
			className={props.className}
			onClick={handleClick}
			style={{
				...props.style,
				cursor: "pointer",
			}}
		>
			{item.accountName}
		</td>
	);
};

const PercentCell = (props: GridCellProps) => {
	const item: IVesselQuotaData = props.dataItem;
	const capped = Math.min(item.bookedQuotaPercentage, 100);
	const color = item.bookedQuotaPercentage > 100 ? "red" : COLOR.BlueLight;
	const fontColor = item.bookedQuotaPercentage > 100 ? "red" : "#555";
	return (
		<td className={props.className}>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<span
					style={{
						color: fontColor,
						width: 37,
					}}
				>
					{item.bookedQuotaPercentage.toFixed(0).replace(".", ",")}%
				</span>
				<div
					style={{
						flex: 1,
						height: 6,
						borderRadius: 3,
						backgroundColor: "#e0e0e0",
						overflow: "hidden",
					}}
				>
					{/* <Slider
						min={0}
						max={100}
						value={capped}
						// disabled={true}
						style={{ width: "100%", backgroundColor: "red" }}
					/> */}
					<div
						style={{
							width: `${capped}%`,
							height: "100%",
							backgroundColor: color,
							borderRadius: 3,
						}}
					/>
				</div>
			</div>
		</td>
	);
};

const NumberCell = (field: keyof IVesselQuotaData) =>
	function Cell(props: GridCellProps) {
		const item: IVesselQuotaData = props.dataItem;
		let color = "";

		const isRemainingField =
			field === "availableRemainingQuota" ||
			field === "potentialRemainingQuota";

		if (isRemainingField && item.fishingStatus === "UNLIMITED") {
			return (
				<td className={props.className} style={{ textAlign: "right" }}>
					FRITT
				</td>
			);
		}

		if (isRemainingField) {
			const value = item[field] as number;
			if (!isNaN(value)) {
				if (value < 0) {
					color = "red";
				} else {
					color = "green";
				}
			}
		}

		return (
			<td className={props.className} style={{ textAlign: "right", color }}>
				{formatNorwegianNumber(item[field] as number)}
			</td>
		);
	};

function VesselQuota({ isMobileView = false }: { isMobileView?: boolean }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { defaultOrg } = useUserOrganizationsStore();
	const [quotaData, setQuotaData] = useState<IVesselQuotaData[]>([]);
	const year = new Date().getFullYear();

	const navigateToTransactions = (item: IVesselQuotaData) => {
		const params = new URLSearchParams({
			accountNumber: item.accountNumber,
			validFrom: item.validFrom,
			validTo: item.validTo ?? "",
			source: item.source ?? "1",
		});
		navigate(`/${PageTypePath.QuotaTransactions}?${params.toString()}`);
	};

	useEffect(() => {
		if (!defaultOrg?.vesselId) return;

		getVesselQuotasApi(
			defaultOrg.vesselId,
			defaultOrg.vesselRegistrationNumber,
			year,
		)
			.then((data) => {
				setQuotaData(data ?? []);
			})
			.catch((err) => {
				console.log(err);
			});
	}, [defaultOrg, year]);

	const DisplayItem = ({
		name,
		value,
		color,
	}: {
		name: string;
		value: string | number;
		color?: string;
	}) => {
		return (
			<div style={{ display: "flex", gap: 4, alignItems: "center" }}>
				<div style={{ fontWeight: 600, fontSize: 14 }}>{name}</div>
				<span>:</span>
				<div
					style={{
						fontSize: 13,
						color: color ?? "#555",
					}}
				>
					{value}
				</div>
			</div>
		);
	};
	return (
		<div>
			<Card className="home-card-item">
				<CardBody>
					<div className={`${styles.BoxHeadingMedium} ${style.CardBodyHeader}`}>
						<div>{t("Quota")}</div>
					</div>

					{isMobileView ? (
						<div>
							{quotaData.map((item, index) => {
								const capped = Math.min(item.bookedQuotaPercentage, 100);
								const color =
									item.bookedQuotaPercentage > 100 ? "red" : COLOR.BlueLight;
								const isUnlimited = item.fishingStatus === "UNLIMITED";
								const remainingColor =
									item.availableRemainingQuota < 0 ? "red" : "#555";
								const potentialRemainingColor =
									item.potentialRemainingQuota < 0 ? "red" : "#555";

								return (
									<div
										key={`${item.accountNumber}-${index}`}
										className="home-mobile-list-item"
										style={{
											flexDirection: "column",
											alignItems: "flex-start",
											gap: 6,
										}}
									>
										<div
											style={{
												fontWeight: 600,
												fontSize: 14,
												cursor: "pointer",
												color: "#0066cc",
												textDecoration: "underline",
											}}
											onClick={() => navigateToTransactions(item)}
										>
											{item.accountName}
										</div>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												width: "100%",
												gap: 16,
												fontSize: 13,
												color: "#555",
											}}
										>
											<DisplayItem
												name={t("Max")}
												value={formatNorwegianNumber(item.maximalQuota)}
											/>
											<DisplayItem
												name={t("BookedQuota")}
												value={formatNorwegianNumber(item.bookedQuota)}
											/>
										</div>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												width: "100%",
												gap: 16,
												fontSize: 13,
												color: "#555",
											}}
										>
											<DisplayItem
												name={t("Remaining")}
												value={
													isUnlimited
														? "FRITT"
														: formatNorwegianNumber(
																item.availableRemainingQuota,
															)
												}
												color={isUnlimited ? undefined : remainingColor}
											/>
											<DisplayItem
												name={t("Potential")}
												value={
													isUnlimited
														? "FRITT"
														: formatNorwegianNumber(
																item.potentialRemainingQuota,
															)
												}
												color={
													isUnlimited ? undefined : potentialRemainingColor
												}
											/>
										</div>

										<div
											style={{
												width: "100%",
												display: "flex",
												alignItems: "center",
												gap: 8,
											}}
										>
											<div
												style={{
													flex: 1,
													height: 6,
													borderRadius: 3,
													backgroundColor: "#e0e0e0",
													overflow: "hidden",
												}}
											>
												<div
													style={{
														width: `${capped}%`,
														height: "100%",
														backgroundColor: color ? color : COLOR.BlueLight,
														borderRadius: 3,
													}}
												/>
											</div>
											<span
												style={{
													fontSize: 13,
													minWidth: 45,
													textAlign: "right",
												}}
											>
												{item.bookedQuotaPercentage
													.toFixed(1)
													.replace(".", ",")}
												%
											</span>
										</div>
										<div
											style={{
												display: "flex",
												gap: 16,
												fontSize: 13,
												color: "#555",
											}}
										>
											<span>{item.quotaArea}</span>
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<Grid
							style={{ height: "auto" }}
							rowHeight={44}
							rows={{ data: (row) => CustomRowRender(row) }}
							data={quotaData}
							className={" custom_detail_grid"}
						>
							<GridColumn
								field="accountName"
								title={t("Quota")}
								className="no-border"
								cells={{ data: AccountNameCell }}
							/>
							<GridColumn
								field="quotaArea"
								title={t("Zone")}
								width="90px"
								className="no-border"
							/>
							<GridColumn
								field="maximalQuota"
								title={t("Max") + ". " + t("Quota")}
								width="120px"
								headerClassName={CLASSNAME.TableNumeric}
								className={`${CLASSNAME.TableNumeric} no-border`}
								cells={{ data: NumberCell("maximalQuota") }}
							/>
							<GridColumn
								field="bookedQuota"
								title={t("BookedQuota")}
								width="110px"
								headerClassName={CLASSNAME.TableNumeric}
								className={`${CLASSNAME.TableNumeric} no-border`}
								cells={{ data: NumberCell("bookedQuota") }}
							/>
							<GridColumn
								field="availableRemainingQuota"
								title={t("Remaining")}
								width="110px"
								headerClassName={CLASSNAME.TableNumeric}
								className={`${CLASSNAME.TableNumeric} no-border`}
								cells={{ data: NumberCell("availableRemainingQuota") }}
							/>
							<GridColumn
								field="potentialRemainingQuota"
								title={t("Potential") + " " + t("Remaining")}
								width="130px"
								headerClassName={CLASSNAME.TableNumeric}
								className={`${CLASSNAME.TableNumeric} no-border`}
								cells={{ data: NumberCell("potentialRemainingQuota") }}
							/>

							<GridColumn
								field="bookedQuotaPercentage"
								title={t("Percentage")}
								// width="200px"

								cells={{ data: PercentCell }}
							/>
						</Grid>
					)}
				</CardBody>
			</Card>
		</div>
	);
}

export default VesselQuota;
